import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography, Chip } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { REGION_IDS, FYLKE_IDS } from 'constants/geo';
import DialogTemplate from 'components/shared/DialogTemplate';
import { Checkbox, Select, Input } from 'components/FormInputs';
import { validatePatched } from 'logic/cottage';
import { useFormik } from 'components/shared/Formik';
import useTextTransformBlur from 'hooks/useTextTransformBlur';
import { capitalizeWords } from 'logic/textTransform';
import SearchBoxMap from './SearchBoxMap';

const defaultLat = 59.334591;
const defaultLng = 18.06324;

const EditPosition = ({ isLoading, onSubmit, onClose, cottage }) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const mapRef = React.useRef(null);
  const [bounds, setBounds] = React.useState(null);
  const [center, setCenter] = React.useState({
    lat: cottage && cottage.lat ? cottage.lat : defaultLat,
    lng: cottage && cottage.lng ? cottage.lng : defaultLng,
  });
  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      region_id: null,
      hide_location: false,
      country: 'SE',
      address_street: '',
      address_postal_code: '',
      address_city: '',
      cottage_village: false,
      property_designation: '',
      ...cottage,
      lat: cottage && cottage.lat ? cottage.lat : null,
      lng: cottage && cottage.lng ? cottage.lng : null,
    },
    enableReinitialize: true,
    validate: validatePatched({ intl, dialog: 'position' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    isSubmitting,
    dirty,
    submitForm,
    setFieldValue,
  } = formik;

  const handleBlur = useTextTransformBlur(formik, {
    address_street: capitalizeWords,
    address_city: capitalizeWords,
  });

  const isNorway = values.country === 'NO';
  const regionOptions = isNorway ? FYLKE_IDS : REGION_IDS;

  // SE län and NO fylker are disjoint id sets, so a region picked under one
  // country is invalid under the other. Clear it when country changes to stop a
  // stale (e.g. Swedish) region_id being submitted for the other country.
  const handleCountryChange = (e) => {
    handleChange(e);
    setFieldValue('region_id', null);
  };

  React.useEffect(() => {
    if (values.lat && values.lng) {
      /* setCenter({
        lat: values.lat,
        lng: values.lng,
      }); */
    }
  }, [values.lat, values.lng]);

  const onPlaceSelected = (place) => {
    if (!place || !place.location) return;
    const c = {
      lat: place.location.lat(),
      lng: place.location.lng(),
    };

    setCenter(c);
    setFieldValue('lat', c.lat);
    setFieldValue('lng', c.lng);
  };

  const onDragEnd = ({ lat, lng }) => {
    setFieldValue('lat', lat);
    setFieldValue('lng', lng);
  };

  const onBoundsChanged = () => {
    setBounds(mapRef.current.getBounds());
  };

  const onMapClick = (e) => {
    const c = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };

    setFieldValue('lat', c.lat);
    setFieldValue('lng', c.lng);
  };

  const setMapRef = (c) => {
    mapRef.current = c;
  };

  const getPropertyDesignationLabel = () => {
    if (values.cottage_village) {
      return (
        <FormattedMessage
          id="Property designation.cottage_village"
          defaultMessage="Cottage number"
        />
      );
    }
    if (values.property_designation_type === 'APARTMENT') {
      return (
        <FormattedMessage id="Property designation.apartment" defaultMessage="Apartment number" />
      );
    }
    return <FormattedMessage id="Property designation" defaultMessage="Property designation" />;
  };
  const getPropertyDesignationHelp = () => {
    if (isNorway) {
      return (
        <FormattedMessage
          id="property_designation_help.no"
          defaultMessage="Property identification, e.g. gnr/bnr (matrikkel)"
        />
      );
    }
    if (values.cottage_village) {
      return (
        <FormattedMessage
          id="property_designation_help.cottage_village"
          defaultMessage="Cottage number (max 5 characters)"
        />
      );
    }
    if (values.property_designation_type === 'APARTMENT') {
      return (
        <FormattedMessage
          id="property_designation_help.apartment"
          defaultMessage="Apartment number (4 digits)"
        />
      );
    }
    return (
      <FormattedMessage
        id="property_designation_help"
        defaultMessage="Property designation (Example: Kommun Traktnamn 10:10)"
      />
    );
  };
  const getPropertyType = () => {
    if (values.property_designation_type === 'APARTMENT') {
      return (
        <FormattedMessage id="property_designation_type.apartment" defaultMessage="Apartment" />
      );
    }
    if (values.property_designation_type === 'COTTAGE') {
      return <FormattedMessage id="property_designation_type.cottage" defaultMessage="Cottage" />;
    }
    return <FormattedMessage id="property_designation_type.house" defaultMessage="House" />;
  };

  return (
    <DialogTemplate
      title={
        <>
          <FormattedMessage id="PositionMap" defaultMessage="Address / Map" />
          &nbsp;
          <>
            #{cottage?.id}
            {!isSmall && <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>}
          </>
        </>
      }
      isUpdating={isLoading || isSubmitting}
      pristine={!dirty}
      onClose={onClose}
      onSave={submitForm}
    >
      <div>
        <Chip label={getPropertyType()} color="primary" size="small" />
        &nbsp; (
        <a href={`/dashboard/cottages/${cottage?.id}/base`}>
          <FormattedMessage id="Change property type" defaultMessage="Change property type" />
        </a>
        )
        <br />
      </div>
      <br />
      <Box pb={4}>
        <form onSubmit={handleSubmit} noValidate>
          <Box mb={2}>
            <Select
              name="country"
              value={values.country}
              errors={errors}
              onChange={handleCountryChange}
              onBlur={handleBlur}
              label={<FormattedMessage id="cottage.country" defaultMessage="Country" />}
              options={[
                {
                  value: 'SE',
                  label: intl.formatMessage({ id: 'cottage.country.se', defaultMessage: 'Sweden' }),
                },
                {
                  value: 'NO',
                  label: intl.formatMessage({ id: 'cottage.country.no', defaultMessage: 'Norway' }),
                },
              ]}
              required
            />
          </Box>
          {cottage?.commission !== false && (
            <Box mb={2}>
              <Typography>
                <FormattedMessage id="cottages.addressDetails" defaultMessage="Address details" />
              </Typography>
              <Input
                name="address_street"
                value={values.address_street}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                label={<FormattedMessage id="Address" defaultMessage="Address" />}
                InputProps={{ inputProps: { maxLength: 255 } }}
                required
              />
              <Box display="flex">
                <Box flex={1} mr={1}>
                  <Input
                    name="address_postal_code"
                    value={values.address_postal_code}
                    onChange={(e) => {
                      e.target.value = (e.target.value || '').replace(/[^0-9]/g, '');
                      return handleChange(e);
                    }}
                    onBlur={handleBlur}
                    errors={errors}
                    label={<FormattedMessage id="Postal Code" defaultMessage="Postal code" />}
                    InputProps={{ inputProps: { maxLength: 50 } }}
                    required
                  />
                </Box>
                <Box flex={2}>
                  <Input
                    name="address_city"
                    value={values.address_city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    label={<FormattedMessage id="CityAlt" defaultMessage="City" />}
                    InputProps={{ inputProps: { maxLength: 100 } }}
                    required
                  />
                </Box>
              </Box>
              <Checkbox
                label={
                  <FormattedMessage id="cottage_village" defaultMessage="Part of cottage village" />
                }
                name="cottage_village"
                onChange={handleChange}
                value={values.cottage_village}
              />
              <Input
                name="property_designation"
                value={values.property_designation}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                label={getPropertyDesignationLabel()}
                InputProps={{ inputProps: { maxLength: 255 } }}
                help={getPropertyDesignationHelp()}
                required
              />
              {values.property_designation_type !== 'APARTMENT' && !values.cottage_village && (
                <>
                  <FormattedMessage
                    id="lantmateriet.linktext"
                    defaultMessage="Find your property designation at "
                  />
                  <a
                    id="lantmateriet-link"
                    href={
                      isNorway
                        ? intl.formatMessage({
                            id: 'kartverket.link',
                            defaultMessage: 'https://seeiendom.kartverket.no/',
                          })
                        : intl.formatMessage({
                            id: 'lantmateriet.link',
                            defaultMessage: 'https://minkarta.lantmateriet.se/',
                          })
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {isNorway ? (
                      <FormattedMessage id="kartverket.text" defaultMessage="Kartverket" />
                    ) : (
                      <FormattedMessage id="lantmateriet.text" defaultMessage="Lantmäteriet" />
                    )}
                  </a>
                </>
              )}
            </Box>
          )}
          <Box mb={4}>
            <Select
              name="region_id"
              value={values.region_id}
              errors={errors}
              onChange={handleChange}
              onBlur={handleBlur}
              label={
                isNorway ? (
                  <FormattedMessage id="Fylke" defaultMessage="County" />
                ) : (
                  <FormattedMessage id="Region" defaultMessage="Region" />
                )
              }
              options={regionOptions.map(({ id, name }) => ({ label: name, value: id }))}
              required
            />
          </Box>
          <Box mb={2}>
            <Typography color={errors.position ? 'error' : 'inherit'}>
              <FormattedMessage id="cottages.mapboxTitle" defaultMessage="Select location in map" />
            </Typography>
            <Box borderColor={errors.position ? '#a5403e' : 'transparent'} border={2}>
              <SearchBoxMap
                position={{
                  lat: values.lat,
                  lng: values.lng,
                }}
                center={center}
                bounds={bounds}
                zoom={cottage && cottage.lat ? 16 : 7}
                onBoundsChanged={onBoundsChanged}
                onDragEnd={onDragEnd}
                onMapMounted={setMapRef}
                onPlaceSelected={onPlaceSelected}
                onClick={onMapClick}
              />
            </Box>
          </Box>
          <hr />
          <Box mt={2} mb={6}>
            <Checkbox
              label={
                <FormattedMessage
                  id="cottages.hideLocation"
                  defaultMessage="Don’t show the location for visitors on the site. Hide map in my ad and exclude ad in map search"
                />
              }
              name="hide_location"
              onChange={handleChange}
              value={values.hide_location}
            />
          </Box>
        </form>
      </Box>
    </DialogTemplate>
  );
};

EditPosition.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    id: PropTypes.number,
    title_sv: PropTypes.string,
    commission: PropTypes.bool,
    country: PropTypes.string,
  }),
};

export default EditPosition;
